import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { SnackbarProvider } from 'notistack';
import { registerSnackbarProvider } from 'src/utils/globalSnackbar';
import infoFill from '@iconify-icons/eva/info-fill';
import alertCircleFill from '@iconify-icons/eva/alert-circle-fill';
import alertTriangleFill from '@iconify-icons/eva/alert-triangle-fill';
import checkmarkCircle2Fill from '@iconify-icons/eva/checkmark-circle-2-fill';
import closeFill from '@iconify-icons/eva/close-fill';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { Box, IconButton } from '@material-ui/core';

// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.mode === 'light';

  const createStyle = {
    color: `${theme.palette.text.primary} !important`,
    backgroundColor: `${theme.palette.background.paper} !important`
  };

  return {
    containerRoot: {
      '& .MuiCollapse-wrapperInner': {
        width: '100%'
      }
    },
    contentRoot: {
      width: '100%',
      padding: theme.spacing(1.5),
      boxShadow: theme.shadows[25].z8,
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.grey[isLight ? 0 : 800],
      backgroundColor: theme.palette.grey[isLight ? 900 : 0]
    },
    message: {
      padding: 0,
      fontWeight: theme.typography.fontWeightMedium
    },
    action: {
      marginRight: -4,
      '& svg': {
        width: 20,
        height: 20,
        opacity: 0.48,
        '&:hover': { opacity: 1 }
      }
    },
    info: { ...createStyle },
    success: { ...createStyle },
    warning: { ...createStyle },
    error: { ...createStyle }
  };
});

// ----------------------------------------------------------------------

function SnackbarIcon({ icon, color }) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: 'flex',
        borderRadius: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16)
      }}
    >
      <Icon icon={icon} width={24} height={24} />
    </Box>
  );
}

NotistackProvider.propTypes = {
  children: PropTypes.node
};

function NotistackProvider({ children }) {
  const classes = useStyles();
  const providerRef = useRef(null);

  // Expone el enqueue globalmente para que el interceptor axios y
  // otros callers fuera de React puedan mostrar snackbars sin tener
  // que colgar de un componente.
  useEffect(() => {
    registerSnackbarProvider(providerRef.current);
    return () => registerSnackbarProvider(null);
  }, []);

  return (
    <SnackbarProvider
      ref={providerRef}
      dense
      maxSnack={5}
      preventDuplicate
      // 2026-09-21 · Antes 3000ms — se percibía bloqueante en flujos
      // rápidos (cobros consecutivos). Bajamos a 2500 y añadimos
      // botón × global para que el user pueda descartar al instante
      // (ver `action` abajo). Individual callers pueden override
      // pasando `autoHideDuration` al `enqueueSnackbar`.
      autoHideDuration={2500}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      iconVariant={{
        success: <SnackbarIcon icon={checkmarkCircle2Fill} color="success" />,
        error: <SnackbarIcon icon={infoFill} color="error" />,
        warning: <SnackbarIcon icon={alertTriangleFill} color="warning" />,
        info: <SnackbarIcon icon={alertCircleFill} color="info" />
      }}
      classes={{
        containerRoot: classes.containerRoot,
        contentRoot: classes.contentRoot,
        message: classes.message,
        action: classes.action,
        variantInfo: classes.info,
        variantSuccess: classes.success,
        variantWarning: classes.warning,
        variantError: classes.error
      }}
      // 2026-09-21 · Botón × global — user reportó que los snackbars
      // sin cerrar bloqueaban la UX en flujos de cobro rápidos. Ahora
      // cualquier snackbar (success/error/warning/info) trae × para
      // que el user descarte al instante sin esperar el autoHide.
      action={(key) => (
        <IconButton
          size="small"
          onClick={() => providerRef.current?.closeSnackbar(key)}
          sx={{ color: 'inherit' }}
          aria-label="Cerrar"
        >
          <Icon icon={closeFill} width={20} height={20} />
        </IconButton>
      )}
    >
      {children}
    </SnackbarProvider>
  );
}

export default NotistackProvider;
